import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics } from "@/lib/analytics";

import { SiteShell } from "@/components/site/shared";
import SiteHome from "@/components/site/SiteHome";

// Route-based code splitting — the marketing homepage downloads ONLY homepage
// code. Admin, checkout (the Whop SDK used to load on every page!), kickoff,
// deck, funnels etc. fetch their own chunk on demand. Biggest mobile-load win.
const KickoffPage = lazy(() => import("@/components/landing/KickoffPage"));
const PaymentFailedPage = lazy(() => import("@/components/landing/PaymentFailedPage"));
const AdminApp = lazy(() => import("@/components/admin/AdminApp"));
const ClientShare = lazy(() => import("@/components/share/ClientShare"));
const ConnectFacebook = lazy(() => import("@/components/connect/ConnectFacebook"));
const GetLeads2Page = lazy(() => import("@/components/site/GetLeads2Page"));
const SiteCheckout = lazy(() => import("@/components/site/SiteCheckout"));
const SiteJoin = lazy(() => import("@/components/site/SiteJoin"));
const SiteReport = lazy(() => import("@/components/site/SiteReport"));
const ClientRoadmap = lazy(() => import("@/components/site/ClientRoadmap"));
const RevampedReportExample = lazy(() => import("@/components/site/RevampedReportExample"));
const SiteAudit = lazy(() => import("@/components/site/SiteAudit"));
const ScanPage = lazy(() => import("@/components/site/ScanPage"));
const AuditStory = lazy(() => import("@/components/site/AuditStory"));
const SiteRebook = lazy(() => import("@/components/site/SiteRebook"));
const OnboardingBooking = lazy(() => import("@/components/site/OnboardingBooking"));
const SiteThanks = lazy(() => import("@/components/site/SiteThanks"));
const SiteSolutions = lazy(() => import("@/components/site/SiteSolutions"));
const SitePricing = lazy(() => import("@/components/site/SitePricing"));
const SiteHowItWorks = lazy(() => import("@/components/site/SiteHowItWorks"));
const SiteTestimonials = lazy(() => import("@/components/site/SiteTestimonials"));
const SiteContact = lazy(() => import("@/components/site/SiteContact"));
const DemoReview = lazy(() => import("@/components/site/DemoReview"));
const PortalApp = lazy(() => import("@/components/portal/PortalApp"));
const PartnerApp = lazy(() => import("@/components/partner/PartnerApp"));
const ProgressPage = lazy(() => import("@/components/progress/ProgressPage"));
const BoostResults = lazy(() => import("@/components/progress/BoostResults"));
const HubApp = lazy(() => import("@/components/hub/HubApp"));
const SiteBlog = lazy(() => import("@/components/site/SiteBlog"));
const VSLPage = lazy(() => import("@/components/site/VSLPage"));

// Shown only for the split second a lazy route chunk downloads.
const PageFallback = (
  <div className="grid min-h-screen place-items-center bg-cream">
    <img src="/slammed-icon.png" alt="" className="h-10 w-10 animate-pulse rounded-xl" />
  </div>
);

function App() {
  const rawPath = typeof window !== "undefined" ? window.location.pathname : "";
  const path = rawPath.replace(/%20/gi, "").replace(/\s+/g, "");
  const isKickoff = path.startsWith("/kickoff");
  const isAdmin = path.startsWith("/admin");
  const isCheckout = path.startsWith("/checkout");
  const isShare = path.startsWith("/share");
  const isProgress = path.startsWith("/progress/");
  const isConnect = path.startsWith("/connect");
  const isAds = path.startsWith("/ads");
  const isPortal = path.startsWith("/portal");
  const isPartner = path === "/partner" || path.startsWith("/partner/");
  const isHub = path === "/hub" || path.startsWith("/hub/");
  useEffect(() => {
    // Anonymous funnel analytics — skip the admin portal and client share pages.
    if (!isAdmin && !isShare && !isConnect && !isPortal && !isProgress && !isHub && !isPartner) {
      initAnalytics(isCheckout ? "checkout" : isKickoff ? "kickoff" : "landing");
    }

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    // Smooth-scroll (Lenis) hijacks the mouse wheel globally — never run it on the
    // admin portal or client share page, or it blocks scrolling inside modals.
    if (isAdmin || isShare || isConnect || isPortal || isProgress || isHub || isPartner) return;

    // Only smooth-scroll on desktop (fine pointer). Load Lenis lazily so it is
    // not part of the initial bundle for anyone.
    const isDesktop = window.matchMedia("(min-width: 1024px) and (pointer: fine)").matches;
    if (!isDesktop) return;

    let lenis;
    let raf;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ lerp: 0.12, wheelMultiplier: 1, smoothWheel: true });
      // Exposed so modals (GetLeads2CheckoutModal, SiteBookModal) can call
      // .stop()/.start() on it — Lenis drives scroll programmatically via
      // requestAnimationFrame, so CSS `overflow:hidden` on html/body alone
      // does not stop the background page from scrolling behind a modal.
      window.__lenis = lenis;
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      lenis?.destroy();
      window.__lenis = null;
    };
  }, []);

  let page;
  if (isAdmin) {
    page = <AdminApp />;
  } else if (isHub) {
    // /hub = client dashboard (email + one-time code): Paige activity, numbers,
    // their links and website-domain setup. Edited from Admin -> People.
    page = <HubApp />;
  } else if (isPortal) {
    // /portal = client-facing CRM (leads pipeline + two-way SMS). Separate auth
    // realm from /admin; accounts are created from the admin CRM tab.
    page = <PortalApp />;
  } else if (isPartner) {
    // /partner = VIP partner portal (separate auth). Referral + payment links,
    // a business-search report tool, and their referred people + commission.
    page = <PartnerApp />;
  } else if (isShare) {
    page = <ClientShare />;
  } else if (isConnect) {
    page = <ConnectFacebook />;
  } else if (isCheckout) {
    // /checkout RETIRED — it embedded the OLD Whop plan (full price, no $1 trial).
    // Everyone on Slammed must start on the $1 flow unless sent a direct link, so
    // bounce any old /checkout link to the current $1 Pro checkout (keep attribution).
    window.location.replace(`/start${window.location.search || "?plan=pro"}`);
    return null;
  } else if (isKickoff) {
    // Bare /kickoff (no token) = the UNIVERSAL post-payment booking link.
    // Safe to text to any buyer or set as Whop's "redirect after checkout" —
    // the backend mints a fresh session and 302s to /kickoff/<token>.
    if (path === "/kickoff" || path === "/kickoff/") {
      window.location.replace(`/api/kickoff/open${window.location.search || ""}`);
      return null;
    }
    // Whop bounced the buyer back but the card was NOT charged → retry page
    page = path.startsWith("/kickoff/payment-failed") ? <PaymentFailedPage /> : <KickoffPage />;
  } else if (isAds) {
    // /ads retired — the old Meta-ads marketing page is deleted (offer is now
    // high-ticket, sold on calls). Old links land on the main site instead.
    window.location.replace("/");
    return null;
  } else if (path.startsWith("/getleads2") || path.startsWith("/getleads")) {
    // /getleads(2) — both serve the same calendar-first, $79 Google Boost
    // lander. The old quiz-first variant (A) and the A/B split are retired;
    // this is now the one and only /getleads experience.
    page = <GetLeads2Page />;
  } else if (path.startsWith("/vsl")) {
    // /vsl = PRIVATE, unlinked one-pager walkthrough of the Google Boost
    // process (rank map, profile rebuild, posts/reviews) for recording a VSL
    // over. Sample data only, names blurred — never linked in nav.
    page = <VSLPage />;
  } else if (path.startsWith("/start")) {
    // /start = dedicated Core checkout page (embedded Whop) -> kickoff booking.
    page = <SiteCheckout />;
  } else if (path.startsWith("/join")) {
    // /join = direct-signup link (in-person / DM'd buyers): details -> payment -> kickoff.
    page = <SiteJoin />;
  } else if (path.startsWith("/progress/")) {
    // /progress/<token> = per-client live Google progress page (public, read-only).
    page = <ProgressPage />;
  } else if (path.startsWith("/results/")) {
    // /results/<token> = Day-1-vs-Day-7 before/after for a Google Boost client (public, read-only).
    page = <BoostResults />;
  } else if (path.startsWith("/report-example")) {
    // Standalone EXAMPLE of the revamped mindmap-style report (does not touch the real report).
    page = <RevampedReportExample />;
  } else if (path.startsWith("/report/")) {
    // /report/<token> = shareable competitor analysis report (public, read-only).
    // (The old /present slide mode is retired — plain report is the shareable view.)
    if (path.endsWith("/present")) {
      window.location.replace(path.replace(/\/present\/?$/, ""));
      return null;
    }
    page = <SiteReport />;
  } else if (path.startsWith("/roadmap/")) {
    // /roadmap/<token> = short, call-only Phase 1 -> 90 -> Phase 2 roadmap (public, read-only).
    page = <ClientRoadmap />;
  } else if (path.startsWith("/scan")) {
    // /scan = home-page instant competitor report (gathering → unlock → /report).
    page = <ScanPage />;
  } else if (/^\/audit\/[A-Za-z0-9_-]+/.test(path)) {
    // /audit/<token> = instant Beside-style story report shown right after the /scan gate.
    page = <AuditStory />;
  } else if (path.startsWith("/audit")) {
    // /audit = self-serve competitor analysis tool (standalone — no site nav).
    page = <SiteAudit />;
  } else if (path.startsWith("/demo/review")) {
    page = <DemoReview />;
  } else if (path.startsWith("/onboarding")) {
    // /onboarding = standalone, calendar-FIRST booking page for cold campaigns.
    // Pick a time, then enter details. Tagged funnel="onboarding" for tracking.
    page = <OnboardingBooking />;
  } else if (path.startsWith("/rebook")) {
    // /rebook = no-quiz rebooking page (details + calendar) for no-show follow-ups.
    page = <SiteRebook />;
  } else if (path.startsWith("/thanks")) {
    // /thanks = post-booking thank-you page (pre-call prep, calendar buttons,
    // ad-conversion tracking). The report stays gatekept for the call.
    page = <SiteThanks />;
  } else {
    // The NEW site (no-ads Google/AI offer) — tabbed pages, book-a-call funnel.
    const sitePage = path.startsWith("/solutions") ? <SiteSolutions />
      : path.startsWith("/how-it-works") ? <SiteHowItWorks />
      : path.startsWith("/pricing") ? <SitePricing />
      : path.startsWith("/testimonials") ? <SiteTestimonials />
      : path.startsWith("/blog") ? <SiteBlog />
      : path.startsWith("/contact") ? <SiteContact />
      : <SiteHome />; // "/"
    const activeTab = path.startsWith("/solutions") ? "solutions"
      : path.startsWith("/how-it-works") ? "how-it-works"
      : path.startsWith("/pricing") ? "pricing"
      : path.startsWith("/testimonials") ? "testimonials"
      : path.startsWith("/blog") ? "blog"
      : path.startsWith("/contact") ? "contact"
      : "home";
    page = <SiteShell active={activeTab}>{sitePage}</SiteShell>;
  }
  return (
    <>
      <Suspense fallback={PageFallback}>{page}</Suspense>
      <Toaster position="top-center" />
    </>
  );
}

export default App;
